import { mergeDeep } from '@/helpers/utils'
import { LandingComponentsConfig, LandingScreenSchema } from '@/modules/Landing/instances/@types/landing-types'
import militaryChairTaiChiMWebp from '@/assets/landing/military-chair-tai-chi-m.webp'
import alertPrintableGuidePng from '@/assets/landing/alert-printable-guide-military-tai-chi.png'
import militaryTaiChi4049Webp from '@/assets/landing-age-selection/military-tai-chi/40-49.webp'
import militaryTaiChi5059Webp from '@/assets/landing-age-selection/military-tai-chi/50-59.webp'
import militaryTaiChi6069Webp from '@/assets/landing-age-selection/military-tai-chi/60-69.webp'
import militaryTaiChi7080Webp from '@/assets/landing-age-selection/military-tai-chi/70-80.webp'

export default (schemas: LandingScreenSchema[]): LandingScreenSchema[] => {
	const extensionSchema: Partial<LandingScreenSchema>[] = [
		{
			// loader-chair-tai-chi-m — "Chair Tai Chi for Beginners" → "Military Chair Tai Chi for Seniors"
			components: {
				LandingTitle: (): LandingComponentsConfig['LandingTitle'] => ({
					title: 'Military Chair Tai Chi for Seniors <br/>Based on Your Age',
					size: 'small',
				}),

				// The base image is full-bleed (calc(100% + 40px) + −20px side margins). The Figma slot
				// (121:635) is 350×339 — plain content width — so the bleed is dropped. Overriding the
				// component fn replaces it wholesale, which is exactly how those margins go away.
				LandingImage: (): LandingComponentsConfig['LandingImage'] => ({
					img: militaryChairTaiChiMWebp,
					customStyles: {
						marginTop: 'var(--size-24)',
						marginBottom: 'var(--size-24)',
					},
				}),

				LandingProgressLoader: (): LandingComponentsConfig['LandingProgressLoader'] => ({
					duration: 3500,
					theme: 'military-tai-chi',
				}),
			},
		},
		{
			// age-chair-tai-chi-m — "CHAIR TAI CHI FOR BEGINNERS" → "MILITARY CHAIR TAI CHI FOR SENIORS"
			components: {
				// Banner colours/padding follow age-military-calisthenics-m-pr: reuse the shared (localized)
				// printable-alert copy, but recolor the "printable guide" highlight to the Figma orange
				// #D17D17 (no DS token matches). Rewriting the span's class→style keeps every locale intact
				// — no new i18n key.
				// The icon, however, is NOT that funnel's camo gift box: Figma Option B (216:2346) shows a
				// brown "Printable Guide" booklet, so it ships as its own asset.
				LandingAlert: ({ $t }): LandingComponentsConfig['LandingAlert'] => ({
					text: $t('female_yoga.pages.landing.alert_text').replace("class='text-primary'", "style='color: #D17D17'"),
					icon: alertPrintableGuidePng,
					// In Figma the booklet is taller than the icon box and runs to the banner's bottom edge
					// (measured on the banner render: icon ink spans y 12..71 of a 72px banner).
					iconBleed: true,
					backgroundColor: 'var(--Fill-Orange-16)',
					customStyles: {
						marginTop: 'var(--size-20)',
						// Figma banner padding: 12px 20px (component default is 16px 20px).
						padding: 'var(--size-12) var(--size-20)',
					},
				}),

				// LandingTitleExtended auto-uppercases the title; overriding the component fn replaces it
				// wholesale, so the `note` must be re-emitted.
				LandingTitleExtended: ({ $t }): LandingComponentsConfig['LandingTitleExtended'] => ({
					title: 'Military CHAIR Tai Chi for Seniors',
					note: $t('female_yoga.pages.landing.quiz'),
				}),

				// Age ranges and labels are unchanged — only the photos and the accent swap. The
				// `otherAgesButton` ("I'm 18-39") is intentionally NOT re-emitted: it is absent from Figma
				// Option B, matching the established military age screens, and the narrative is "for Seniors".
				LandingAgeGrid: ({ $t }): LandingComponentsConfig['LandingAgeGrid'] => ({
					theme: 'military-tai-chi',
					ageRanges: [
						{
							value: 40,
							analyticValue: '40-49',
							label: $t('female_yoga.pages.landing.age_range.40_49'),
							img: militaryTaiChi4049Webp,
						},
						{
							value: 50,
							analyticValue: '50-59',
							label: $t('female_yoga.pages.landing.age_range.50_59'),
							img: militaryTaiChi5059Webp,
						},
						{
							value: 60,
							analyticValue: '60-69',
							label: $t('female_yoga.pages.landing.age_range.60_69'),
							img: militaryTaiChi6069Webp,
						},
						{
							value: 70,
							analyticValue: '70+',
							label: $t('female_yoga.pages.landing.age_range.70_80'),
							img: militaryTaiChi7080Webp,
						},
					],
				}),
			},
		},
	]

	return mergeDeep([...schemas], extensionSchema)
}
